import React from 'react';
import { Button } from '../../ui/button';
import {
  Shield, Key, CheckCircle, Heart, Download, Send, Unlock, Eye,
  AlertCircle, Link, User, FileText, Stethoscope, Activity,
  CreditCard, Database, Lock
} from 'lucide-react';

export const OverviewTab = ({ linkedAbha, eligibility, consents, nationalRecords, claims, handleCheckEligibility, setActiveTab, setShowClaimForm }) => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div className={`p-6 rounded-xl border-2 ${linkedAbha ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
        <div className="flex items-center gap-3 mb-4">
          <Key className={`w-6 h-6 ${linkedAbha ? 'text-green-600' : 'text-yellow-600'}`} />
          <h3 className="font-semibold text-lg">ABHA Status</h3>
        </div>
        {linkedAbha ? (
          <div>
            <p className="text-green-800 font-medium">{linkedAbha.abha_number}</p>
            <p className="text-green-600 text-sm">{linkedAbha.abha_address}</p>
          </div>
        ) : (
          <p className="text-yellow-700">No ABHA linked. Click to setup.</p>
        )}
      </div>
      <div className={`p-6 rounded-xl border-2 ${eligibility?.is_eligible ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-3 mb-4">
          <Heart className={`w-6 h-6 ${eligibility?.is_eligible ? 'text-green-600' : 'text-gray-500'}`} />
          <h3 className="font-semibold text-lg">Ayushman Bharat</h3>
        </div>
        {eligibility?.is_eligible ? (
          <div>
            <p className="text-green-800 font-medium">Eligible for PMJAY</p>
            <p className="text-green-600 text-sm">Coverage: &#8377;{(eligibility.eligibility_details?.coverage_amount || 0).toLocaleString()}</p>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-gray-600">Check eligibility</p>
            <Button size="sm" onClick={handleCheckEligibility} disabled={!linkedAbha}>Check Now</Button>
          </div>
        )}
      </div>
    </div>
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="font-semibold mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveTab('records')} disabled={!linkedAbha}>
          <Download className="w-5 h-5 mb-2" /><span className="text-xs">Fetch Records</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setShowClaimForm(true)} disabled={!eligibility?.is_eligible}>
          <Send className="w-5 h-5 mb-2" /><span className="text-xs">Submit Claim</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveTab('consents')}>
          <Unlock className="w-5 h-5 mb-2" /><span className="text-xs">Manage Consent</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveTab('claims')}>
          <Eye className="w-5 h-5 mb-2" /><span className="text-xs">Track Claims</span>
        </Button>
      </div>
    </div>
  </div>
);

export const AbhaTab = ({ linkedAbha, setShowRegistration, setShowLinking }) => (
  <div className="space-y-6">
    {linkedAbha ? (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <div>
            <p className="text-sm text-green-600 font-medium">ABHA Linked</p>
            <p className="text-2xl font-bold text-gray-900">{linkedAbha.abha_number}</p>
            <p className="text-gray-600">{linkedAbha.abha_address}</p>
          </div>
        </div>
      </div>
    ) : (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">No ABHA Linked</p>
            <p className="text-sm text-yellow-600">Link or create an ABHA to enable ABDM features</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowRegistration(true)}>
            <User className="w-4 h-4 mr-2" />Create New ABHA
          </Button>
          <Button variant="outline" onClick={() => setShowLinking(true)}>
            <Link className="w-4 h-4 mr-2" />Link Existing ABHA
          </Button>
        </div>
      </div>
    )}
  </div>
);

export const EligibilityTab = ({ eligibility, linkedAbha, handleCheckEligibility }) => (
  <div className="space-y-6">
    {eligibility?.is_eligible ? (
      <>
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-green-800">Eligible for Ayushman Bharat</h3>
                <p className="text-green-600">Pradhan Mantri Jan Arogya Yojana (PMJAY)</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Beneficiary ID</p>
              <p className="font-mono font-bold">{eligibility.eligibility_details?.beneficiary_id}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-500">Coverage Amount</p>
              <p className="text-2xl font-bold text-green-600">&#8377;{(eligibility.eligibility_details?.coverage_amount || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-500">Remaining Balance</p>
              <p className="text-2xl font-bold text-blue-600">&#8377;{(eligibility.eligibility_details?.remaining_amount || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-500">Valid Until</p>
              <p className="text-2xl font-bold text-gray-700">{eligibility.eligibility_details?.valid_until}</p>
            </div>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Covered Packages</h4>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {eligibility.covered_packages?.map((pkg) => (
              <div key={pkg.code} className="bg-gray-50 rounded-lg p-4 border">
                <p className="font-medium">{pkg.name}</p>
                <p className="text-sm text-gray-500">Code: {pkg.code}</p>
                <p className="text-green-600 font-semibold">Up to &#8377;{pkg.limit.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </>
    ) : (
      <div className="text-center py-12">
        <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Check Ayushman Bharat Eligibility</h3>
        <p className="text-gray-500 mb-6">Verify if the patient is eligible for PMJAY scheme benefits</p>
        <Button className="bg-green-600 hover:bg-green-700" onClick={handleCheckEligibility} disabled={!linkedAbha}>
          <CheckCircle className="w-4 h-4 mr-2" />Check Eligibility
        </Button>
        {!linkedAbha && <p className="text-sm text-yellow-600 mt-3">Link ABHA first to check eligibility</p>}
      </div>
    )}
  </div>
);

export const RecordsTab = ({ nationalRecords, linkedAbha, handleFetchNationalRecords }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold">National Health Records</h3>
      <Button onClick={handleFetchNationalRecords} disabled={!linkedAbha}>
        <Download className="w-4 h-4 mr-2" />Fetch Records
      </Button>
    </div>
    {nationalRecords.length > 0 ? (
      <div className="space-y-4">
        {nationalRecords.map((record, idx) => (
          <div key={`record-${record.type}-${idx}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  record.type === 'OPConsultation' ? 'bg-blue-100 text-blue-600' :
                  record.type === 'Prescription' ? 'bg-green-100 text-green-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {record.type === 'OPConsultation' ? <Stethoscope className="w-5 h-5" /> :
                   record.type === 'Prescription' ? <FileText className="w-5 h-5" /> :
                   <Activity className="w-5 h-5" />}
                </div>
                <div>
                  <p className="font-medium">{record.type}</p>
                  <p className="text-sm text-gray-500">{record.source_hip} &bull; {record.date}</p>
                </div>
              </div>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{record.source_hip}</span>
            </div>
            {record.diagnosis && <p className="mt-3 text-sm text-gray-700"><strong>Diagnosis:</strong> {record.diagnosis}</p>}
            {record.medications && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700">Medications:</p>
                <ul className="text-sm text-gray-600 ml-4 list-disc">
                  {record.medications.map((med) => <li key={`${med.name}-${med.dosage}`}>{med.name} - {med.dosage}</li>)}
                </ul>
              </div>
            )}
            {record.results && (
              <div className="mt-3 bg-gray-50 rounded p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">{record.test_name} Results:</p>
                {Object.entries(record.results).map(([key, value]) => <p key={key} className="text-sm text-gray-600">{key}: {value}</p>)}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-12 text-gray-500">
        <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No national health records fetched yet</p>
        <p className="text-sm">Click &quot;Fetch Records&quot; to retrieve records from other facilities</p>
      </div>
    )}
  </div>
);

export const ClaimsTab = ({ claims, eligibility, setShowClaimForm }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold">Claims Tracking</h3>
      <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowClaimForm(true)} disabled={!eligibility?.is_eligible}>
        <Send className="w-4 h-4 mr-2" />Submit New Claim
      </Button>
    </div>
    {claims.length > 0 && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{claims.length}</p>
          <p className="text-sm text-blue-700">Total Claims</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{claims.filter(c => c.status === 'SUBMITTED').length}</p>
          <p className="text-sm text-yellow-700">Pending</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{claims.filter(c => c.status === 'APPROVED' || c.status === 'SETTLED').length}</p>
          <p className="text-sm text-green-700">Approved</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">&#8377;{claims.reduce((sum, c) => sum + (c.amounts?.approved || 0), 0).toLocaleString()}</p>
          <p className="text-sm text-gray-700">Total Approved</p>
        </div>
      </div>
    )}
    {claims.length > 0 ? (
      <div className="space-y-3">
        {claims.map((claim) => {
          const statusColors = {
            APPROVED: { border: 'border-green-200 bg-green-50', badge: 'bg-green-200 text-green-800' },
            SETTLED: { border: 'border-blue-200 bg-blue-50', badge: 'bg-blue-200 text-blue-800' },
            SUBMITTED: { border: 'border-yellow-200 bg-yellow-50', badge: 'bg-yellow-200 text-yellow-800' },
            REJECTED: { border: 'border-red-200 bg-red-50', badge: 'bg-red-200 text-red-800' },
          };
          const colors = statusColors[claim.status] || { border: 'border-gray-200', badge: 'bg-gray-200 text-gray-800' };
          return (
            <div key={claim.id} className={`border rounded-lg p-4 ${colors.border}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>{claim.status}</span>
                    <span className="font-mono font-semibold">{claim.claim_number}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{claim.claim_type.toUpperCase()} &bull; {claim.package?.name}</p>
                  <p className="text-xs text-gray-400 mt-1">Submitted: {new Date(claim.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Estimated</p>
                  <p className="font-semibold">&#8377;{claim.amounts?.estimated?.toLocaleString()}</p>
                  {claim.amounts?.approved && (
                    <>
                      <p className="text-sm text-green-600 mt-1">Approved</p>
                      <p className="font-semibold text-green-700">&#8377;{claim.amounts.approved.toLocaleString()}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="text-center py-12 text-gray-500">
        <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No claims submitted yet</p>
      </div>
    )}
  </div>
);

export const ConsentsTab = ({ consents, handleConsentAction }) => (
  <div className="space-y-6">
    <h3 className="font-semibold">Consent Management</h3>
    {consents.length === 0 ? (
      <div className="text-center py-12 text-gray-500">
        <Lock className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No consent requests</p>
      </div>
    ) : (
      <div className="space-y-3">
        {consents.map((consent) => {
          const statusColors = {
            GRANTED: { border: 'border-green-200 bg-green-50', badge: 'bg-green-200 text-green-800' },
            REQUESTED: { border: 'border-yellow-200 bg-yellow-50', badge: 'bg-yellow-200 text-yellow-800' },
            DENIED: { border: 'border-red-200 bg-red-50', badge: 'bg-red-200 text-red-800' },
          };
          const colors = statusColors[consent.status] || { border: 'border-gray-200 bg-gray-50', badge: 'bg-gray-200 text-gray-800' };
          return (
            <div key={consent.id} className={`border rounded-lg p-4 ${colors.border}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>{consent.status}</span>
                    <span className="font-medium text-gray-800">{consent.purpose}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Requester: {consent.requester?.name} ({consent.requester?.type})</p>
                  <p className="text-xs text-gray-500 mt-1">Data: {consent.hi_types?.join(', ')}</p>
                </div>
                <div className="flex gap-2">
                  {consent.status === 'REQUESTED' && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleConsentAction(consent.id, 'approve')}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => handleConsentAction(consent.id, 'deny')}>Deny</Button>
                    </>
                  )}
                  {consent.status === 'GRANTED' && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleConsentAction(consent.id, 'revoke')}>Revoke</Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);
